import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Operator command palette (⌘K / Ctrl-K).
 *
 * A single keystroke opens a fuzzy search that blends three worlds:
 *   • Actions   — verb intents (create org, manage venues, …)
 *   • Navigate  — jump to any operator page
 *   • Live data — organizations, events, venues (fetched on first open)
 *
 * Fully keyboard-driven: ↑/↓ to move, Enter to run, Esc to close. Results are
 * grouped and ranked by a lightweight fuzzy/substring score.
 */

type Group = 'Actions' | 'Navigate' | 'Organizations' | 'Events' | 'Venues';

interface Command {
  id: string;
  group: Group;
  title: string;
  subtitle?: string;
  icon: string;
  keywords?: string;
  run: () => void;
}

interface OrgRow { id: string; name: string; slug: string; status: string }
interface EventRow { id: string; name: string; tenantName: string; tenantId: string; status: string }
interface VenueRow { id: string; name: string; city: string; state: string; tenantName: string }

/** Simple subsequence fuzzy score: higher is better; -1 means no match. */
function score(query: string, text: string): number {
  if (!query) return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  const idx = t.indexOf(q);
  if (idx >= 0) return 1000 - idx; // contiguous match, earlier = better
  // subsequence
  let qi = 0;
  let s = 0;
  let last = -1;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) {
      s += last === i - 1 ? 3 : 1; // reward adjacency
      last = i;
      qi++;
    }
  }
  return qi === q.length ? s : -1;
}

export function CommandPalette({
  open,
  onClose,
  onCreateOrg,
}: {
  open: boolean;
  onClose: () => void;
  onCreateOrg: () => void;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'PlatformAdmin' || user?.role === 'Admin';

  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [venues, setVenues] = useState<VenueRow[]>([]);
  const [loadedData, setLoadedData] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Load live data once, lazily, the first time the palette opens.
  useEffect(() => {
    if (!open || loadedData) return;
    let cancelled = false;
    (async () => {
      try {
        const [o, e, v] = await Promise.all([
          apiClient<OrgRow[]>('/platform/tenants').catch(() => []),
          apiClient<EventRow[]>('/platform/events').catch(() => []),
          apiClient<VenueRow[]>('/platform/venues').catch(() => []),
        ]);
        if (!cancelled) {
          setOrgs(o || []);
          setEvents(e || []);
          setVenues(v || []);
          setLoadedData(true);
        }
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [open, loadedData]);

  // Reset + focus on open.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  const go = (path: string) => { onClose(); navigate(path); };

  const commands = useMemo<Command[]>(() => {
    const actions: Command[] = [
      ...(isAdmin ? [{
        id: 'act-create-org', group: 'Actions' as Group, title: 'Create organization',
        subtitle: 'Onboard a new customer', icon: '➕', keywords: 'new add tenant customer',
        run: () => { onClose(); onCreateOrg(); },
      }] : []),
      { id: 'act-venues', group: 'Actions', title: 'Manage venues', subtitle: 'Add, edit, assign', icon: '📍', keywords: 'venue location', run: () => go('/platform/venues') },
      ...(isAdmin ? [{ id: 'act-operators', group: 'Actions' as Group, title: 'Manage operators', subtitle: 'Platform staff', icon: '🛡️', keywords: 'users staff admin', run: () => go('/platform/operators') }] : []),
    ];
    const nav: Command[] = [
      { id: 'nav-overview', group: 'Navigate', title: 'Overview', icon: '📊', keywords: 'home dashboard', run: () => go('/platform') },
      { id: 'nav-orgs', group: 'Navigate', title: 'Organizations', icon: '🏢', keywords: 'tenants customers', run: () => go('/platform/organizations') },
      { id: 'nav-events', group: 'Navigate', title: 'Events', icon: '📅', keywords: '', run: () => go('/platform/events') },
      { id: 'nav-giving', group: 'Navigate', title: 'Giving', icon: '💚', keywords: 'fundraising campaigns donations', run: () => go('/platform/fundraising') },
      { id: 'nav-venues', group: 'Navigate', title: 'Venues', icon: '📍', keywords: '', run: () => go('/platform/venues') },
      { id: 'nav-workflows', group: 'Navigate', title: 'Workflows', icon: '📋', keywords: '', run: () => go('/platform/workflows') },
      { id: 'nav-capabilities', group: 'Navigate', title: 'Capabilities', icon: '🧩', keywords: 'modules', run: () => go('/platform/capabilities') },
      { id: 'nav-demos', group: 'Navigate', title: 'System & Process Demos', icon: '🎬', keywords: 'demo', run: () => go('/platform/demos') },
    ];
    const orgCmds: Command[] = orgs.map((o) => ({
      id: `org-${o.id}`, group: 'Organizations', title: o.name, subtitle: `${o.slug} · ${o.status}`, icon: '🏢',
      keywords: `${o.slug} organization tenant`, run: () => go(`/platform/organizations/${o.slug || o.id}`),
    }));
    const eventCmds: Command[] = events.map((e) => ({
      id: `evt-${e.id}`, group: 'Events', title: e.name, subtitle: `${e.tenantName} · ${e.status}`, icon: '📅',
      keywords: e.tenantName, run: () => go(`/platform/organizations/${e.tenantId}`),
    }));
    const venueCmds: Command[] = venues.map((v) => ({
      id: `ven-${v.id}`, group: 'Venues', title: v.name, subtitle: `${v.city}, ${v.state} · ${v.tenantName}`, icon: '📍',
      keywords: `${v.city} ${v.state} ${v.tenantName}`, run: () => go('/platform/venues'),
    }));
    return [...actions, ...nav, ...orgCmds, ...eventCmds, ...venueCmds];
  }, [isAdmin, orgs, events, venues]);

  const filtered = useMemo(() => {
    if (!query.trim()) {
      // Default view: actions + nav only (don't dump all data).
      return commands.filter((c) => c.group === 'Actions' || c.group === 'Navigate');
    }
    const scored = commands
      .map((c) => ({ c, s: Math.max(score(query, c.title), score(query, c.keywords || '') - 2, score(query, c.subtitle || '') - 4) }))
      .filter((x) => x.s > -1)
      .sort((a, b) => b.s - a.s)
      .slice(0, 40);
    return scored.map((x) => x.c);
  }, [query, commands]);

  // Keep active index in range; scroll into view.
  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); filtered[active]?.run(); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  // Group the filtered results in display order.
  const groupsOrder: Group[] = ['Actions', 'Navigate', 'Organizations', 'Events', 'Venues'];
  let runningIdx = -1;

  return (
    <div style={styles.scrim} onMouseDown={onClose}>
      <div style={styles.palette} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Command palette">
        <div style={styles.inputRow}>
          <span style={styles.searchIcon}>⌘</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search organizations, events, venues — or type a command…"
            style={styles.input}
            aria-label="Command search"
          />
          <kbd style={styles.escHint}>esc</kbd>
        </div>

        <div style={styles.results} ref={listRef}>
          {filtered.length === 0 ? (
            <div style={styles.empty}>No matches for “{query}”.</div>
          ) : (
            groupsOrder.map((g) => {
              const items = filtered.filter((c) => c.group === g);
              if (items.length === 0) return null;
              return (
                <div key={g}>
                  <div style={styles.groupLabel}>{g}</div>
                  {items.map((c) => {
                    runningIdx += 1;
                    const idx = runningIdx;
                    const isActive = idx === active;
                    return (
                      <div
                        key={c.id}
                        data-idx={idx}
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => c.run()}
                        style={{ ...styles.item, ...(isActive ? styles.itemActive : null) }}
                      >
                        <span style={{ ...styles.itemIcon, ...(isActive ? styles.itemIconActive : null) }}>{c.icon}</span>
                        <span style={styles.itemBody}>
                          <span style={styles.itemTitle}>{c.title}</span>
                          {c.subtitle && <span style={styles.itemSub}>{c.subtitle}</span>}
                        </span>
                        {isActive && <span style={styles.enterHint}>↵</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        <div style={styles.footer}>
          <span><kbd style={styles.kbd}>↑</kbd><kbd style={styles.kbd}>↓</kbd> navigate</span>
          <span><kbd style={styles.kbd}>↵</kbd> select</span>
          <span><kbd style={styles.kbd}>esc</kbd> close</span>
          {!loadedData && <span style={styles.loadingDot}>loading data…</span>}
        </div>
      </div>
    </div>
  );
}

const ACCENT = '#6366f1';
const styles: Record<string, React.CSSProperties> = {
  scrim: { position: 'fixed', inset: 0, background: 'rgba(8,11,22,0.55)', backdropFilter: 'blur(3px)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh' },
  palette: { width: 'min(640px, 94vw)', background: '#fff', borderRadius: 16, boxShadow: '0 30px 80px -20px rgba(8,11,22,0.6)', overflow: 'hidden', border: '1px solid #e8eaf0', animation: 'none' },
  inputRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px', borderBottom: '1px solid #eef0f4' },
  searchIcon: { fontSize: '1rem', color: ACCENT, fontWeight: 800 },
  input: { flex: 1, border: 'none', outline: 'none', fontSize: '1.05rem', color: '#0b1120', background: 'transparent' },
  escHint: { fontSize: '0.68rem', color: '#9aa4b2', border: '1px solid #e5e7eb', borderRadius: 5, padding: '2px 6px' },
  results: { maxHeight: '52vh', overflowY: 'auto', padding: '8px 8px 12px' },
  groupLabel: { fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9aa4b2', padding: '10px 12px 4px' },
  item: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, cursor: 'pointer' },
  itemActive: { background: 'linear-gradient(90deg, #eef2ff, #f5f3ff)' },
  itemIcon: { width: 32, height: 32, borderRadius: 9, display: 'grid', placeItems: 'center', background: '#f1f5f9', fontSize: '1rem', flexShrink: 0 },
  itemIconActive: { background: '#fff', boxShadow: `0 2px 8px -2px ${ACCENT}66` },
  itemBody: { display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 },
  itemTitle: { fontSize: '0.92rem', fontWeight: 700, color: '#0b1120', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  itemSub: { fontSize: '0.76rem', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  enterHint: { color: ACCENT, fontWeight: 800, fontSize: '0.9rem' },
  empty: { padding: '28px 16px', textAlign: 'center', color: '#9aa4b2', fontSize: '0.9rem' },
  footer: { display: 'flex', gap: 16, alignItems: 'center', padding: '10px 16px', borderTop: '1px solid #eef0f4', fontSize: '0.72rem', color: '#9aa4b2' },
  kbd: { border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 5px', margin: '0 2px', fontSize: '0.7rem', color: '#6b7280' },
  loadingDot: { marginLeft: 'auto', color: ACCENT, fontWeight: 600 },
};
